import React from 'react';

import keyBy from 'lodash/keyBy';

import { Box, Chip, Stack, Typography } from '@mui/material';

import { getListItemStatus, sortFacilities } from './arrayDiff';
import { formatItem, formatSimpleValue, getBedGroupItems, isBedGroupItem, isFeaturedFacility } from './formatters';
import { getImagePreview, getImageTitle } from './imageUtils';
import type { ListItemStatus } from './types';
import { itemIdentity } from './utils';

export function getListItemSx(item: unknown, status: ListItemStatus | undefined) {
  if (status === 'added') {
    return {
      bgcolor: 'success.50',
      border: '1px solid',
      borderColor: 'success.light',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  if (status === 'removed') {
    return {
      bgcolor: 'error.50',
      border: '1px solid',
      borderColor: 'error.light',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  if (status === 'changed') {
    return {
      bgcolor: 'info.50',
      border: '1px solid',
      borderColor: 'info.light',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  if (status === 'reordered') {
    return {
      bgcolor: 'grey.100',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  if (status === 'featured-added') {
    return {
      bgcolor: 'warning.50',
      border: '1px solid',
      borderColor: 'warning.light',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  if (status === 'featured-removed') {
    return {
      bgcolor: 'grey.100',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  if (isFeaturedFacility(item)) {
    return {
      bgcolor: 'warning.50',
      border: '1px solid',
      borderColor: 'warning.light',
      borderRadius: 1,
      px: 1,
      py: 0.75,
    };
  }

  return undefined;
}

export function getListItemStatusChip(status: ListItemStatus | undefined): React.ReactNode {
  if (status === 'added') {
    return <Chip size="small" color="success" variant="outlined" label="Added" />;
  }
  if (status === 'removed') {
    return <Chip size="small" color="error" variant="outlined" label="Removed" />;
  }
  if (status === 'changed') {
    return <Chip size="small" color="info" variant="outlined" label="Changed" />;
  }
  if (status === 'reordered') {
    return <Chip size="small" color="default" variant="outlined" label="Reordered" />;
  }
  if (status === 'featured-added') {
    return <Chip size="small" color="warning" variant="outlined" label="Now featured" />;
  }
  if (status === 'featured-removed') {
    return <Chip size="small" color="default" variant="outlined" label="No longer featured" />;
  }

  return null;
}

function renderList(
  value: unknown,
  label: string,
  options: { compareItems?: Array<unknown>; panel?: 'before' | 'after' } = {},
): React.ReactNode {
  if (!Array.isArray(value)) {
    return null;
  }
  const items = sortFacilities(value);
  const imageAltPrefix = label.split(' ')[0] ?? label;
  const { compareItems, panel } = options;
  const compareByIdentity = compareItems ? keyBy(compareItems, itemIdentity) : undefined;

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }

  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {items.map((item, index) => {
        const status = getListItemStatus(item, compareByIdentity, panel);

        return (
          <Box key={`${itemIdentity(item)}-${index}`} component="li" sx={getListItemSx(item, status)}>
            <Stack spacing={0.75}>
              {getImagePreview(item, `${imageAltPrefix} ${getImageTitle(item, `Image ${index + 1}`)}`)}
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Typography variant="body2">{formatItem(item)}</Typography>
                {getListItemStatusChip(status)}
                {isFeaturedFacility(item) && status !== 'featured-added' && status !== 'featured-removed' && (
                  <Chip size="small" color="warning" variant="outlined" label="Featured" />
                )}
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function renderBedGroupList(value: unknown): React.ReactNode {
  if (!Array.isArray(value)) {
    return null;
  }

  if (value.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }

  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {value.map((item, index) => (
        <Box
          key={`${itemIdentity(item)}-${index}`}
          component="li"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
          }}
        >
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {`Bed group ${index + 1}`}
            </Typography>
            <Stack spacing={0.5}>
              {isBedGroupItem(item) ? (
                getBedGroupItems(item).map((bed, bedIndex) => (
                  <Typography key={`${bed}-${bedIndex}`} variant="body2">
                    {bed}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">{formatItem(item)}</Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function isStandaloneImageField(key: string): boolean {
  return key === 'logo_id_cloudinary_external';
}

export function renderDisplayValue(
  value: unknown,
  key: string,
  label: string,
  options: { allowStringImageId?: boolean; compareValue?: unknown; panel?: 'before' | 'after' } = {},
): React.ReactNode {
  const { allowStringImageId = false, compareValue, panel } = options;

  if (key === 'bed_groups' && Array.isArray(value)) {
    return renderBedGroupList(value);
  }
  if (Array.isArray(value)) {
    return renderList(value, label, {
      compareItems: Array.isArray(compareValue) ? compareValue : undefined,
      panel,
    });
  }

  const imagePreview = getImagePreview(value, label, allowStringImageId);

  return (
    <Stack spacing={0.75}>
      {imagePreview}
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {formatSimpleValue(value)}
      </Typography>
    </Stack>
  );
}

export function isStandaloneImageKey(key: string): boolean {
  return isStandaloneImageField(key);
}
